/**
 * Rollup Config Menu - Column Header Configuration
 *
 * Allows users to configure rollup columns:
 * 1. Select source relation (which relation column to use)
 * 2. Select target field (which field from the related database to show)
 * 3. Select aggregation (how to combine multiple values)
 *
 * E018-S008-T009: Initial implementation with ConfigDropdown
 */
import '../shared/config-dropdown';

import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import type { Store } from '@blocksuite/store';
import { css, html } from 'lit';
import { property, state } from 'lit/decorators.js';

import {
  findDatabaseByBlockId,
  isBridgeInitialized,
} from '../relation/database-discovery-bridge';
import type { DropdownOption } from '../shared/config-dropdown';
import { AGGREGATION_META } from './aggregation';
import type { RollupAggregation, RollupPropertyData } from './define';

// Icons
const RollupIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 16 16"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M3 4H13M3 8H10M3 12H7"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
  />
</svg>`;

interface RelationColumnInfo {
  id: string;
  name: string;
  targetDatabaseId?: string;
  targetDocId?: string;
}

interface TargetFieldInfo {
  id: string;
  name: string;
  type: string;
}

export class RollupConfigMenu extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .rollup-config {
      padding: 8px;
      min-width: 280px;
      font-family: var(--streamlain-font-family);
    }

    .rollup-config-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 8px;
      margin-bottom: 8px;
      color: var(--streamlain-text-primary-color);
      font-size: 14px;
      font-weight: 500;
    }

    .rollup-config-header svg {
      color: var(--streamlain-icon-color);
    }

    .rollup-config-section {
      margin-bottom: 12px;
    }

    .rollup-config-label {
      font-size: 12px;
      font-weight: 500;
      color: var(--streamlain-text-secondary-color);
      margin-bottom: 6px;
      padding: 0 4px;
      display: block;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .rollup-config-hint {
      font-size: 11px;
      color: var(--streamlain-text-secondary-color);
      padding: 6px 4px 0;
      line-height: 1.4;
    }

    .rollup-config-divider {
      height: 1px;
      background: var(--streamlain-border-color);
      margin: 12px 0;
    }

    .rollup-config-save {
      width: 100%;
      padding: 10px 16px;
      background: var(--streamlain-primary-color);
      color: var(--streamlain-pure-white);
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      font-family: var(--streamlain-font-family);
      transition:
        opacity 0.15s ease,
        transform 0.1s ease;
    }

    .rollup-config-save:hover:not(:disabled) {
      opacity: 0.9;
    }

    .rollup-config-save:active:not(:disabled) {
      transform: scale(0.98);
    }

    .rollup-config-save:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .rollup-config-empty {
      padding: 16px;
      text-align: center;
      color: var(--streamlain-text-secondary-color);
      font-size: 13px;
    }
  `;

  @property({ attribute: false })
  accessor store!: Store;

  @property({ attribute: false })
  accessor currentDatabaseId!: string;

  @property({ attribute: false })
  accessor propertyId!: string;

  @property({ attribute: false })
  accessor data!: RollupPropertyData;

  @property({ attribute: false })
  accessor onUpdate!: (data: Partial<RollupPropertyData>) => void;

  @property({ attribute: false })
  accessor onClose: (() => void) | undefined;

  @state()
  accessor _selectedRelationId = '';

  @state()
  accessor _selectedTargetFieldId = '';

  @state()
  accessor _selectedAggregation: RollupAggregation = 'show_original';

  @state()
  accessor _targetFields: TargetFieldInfo[] = [];

  @state()
  accessor _relationColumns: RelationColumnInfo[] = [];

  override connectedCallback() {
    super.connectedCallback();
    this._loadRelationColumns();
    this._syncFromData();
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('data') && this.data) {
      this._syncFromData();
    }
  }

  private _syncFromData() {
    if (!this.data) return;
    this._selectedRelationId = this.data.sourceRelationFieldId || '';
    this._selectedTargetFieldId = this.data.targetFieldId || '';
    this._selectedAggregation = this.data.aggregation || 'show_original';

    if (this._selectedRelationId) {
      this._loadTargetFields().catch(console.error);
    }
  }

  /**
   * Load all relation columns in the current database
   */
  private _loadRelationColumns() {
    const relations: RelationColumnInfo[] = [];

    try {
      const databases = this.store.getBlocksByFlavour('affine:database');
      const currentDb = databases.find(
        (db: { id: string }) => db.id === this.currentDatabaseId
      );

      if (!currentDb) return;

      const model = (currentDb as { model?: unknown }).model ?? currentDb;
      const {
        createDatabaseBlockDataSource,
      } = require('@blocksuite/affine/blocks/database');
      const dataSource = createDatabaseBlockDataSource(model);
      const properties = dataSource.getBoundedPropertyIdsWindow()
        .propertyIds as string[];

      for (const propId of properties) {
        const propType = dataSource.propertyTypeGet(propId);
        if (propType === 'relation') {
          const propData = dataSource.propertyDataGet(propId) as {
            targetDatabaseId?: string;
            targetDocId?: string;
          };
          relations.push({
            id: propId,
            name: dataSource.propertyNameGet(propId) || 'Unnamed Relation',
            targetDatabaseId: propData?.targetDatabaseId,
            targetDocId: propData?.targetDocId,
          });
        }
      }
    } catch (e) {
      console.error('[RollupConfig] Error loading relation columns:', e);
    }

    this._relationColumns = relations;
  }

  /**
   * Load target fields when a relation is selected
   */
  private async _loadTargetFields() {
    const relation = this._relationColumns.find(
      r => r.id === this._selectedRelationId
    );
    if (!relation?.targetDatabaseId) {
      this._targetFields = [];
      return;
    }

    const fields: TargetFieldInfo[] = [];

    try {
      const isCrossDoc =
        relation.targetDocId && relation.targetDocId !== this.store.id;

      // Types that cannot be rolled up
      const excludedTypes = ['relation', 'rollup'];

      if (isCrossDoc && isBridgeInitialized()) {
        const dbInfo = findDatabaseByBlockId(relation.targetDatabaseId);
        if (dbInfo) {
          fields.push({ id: 'title', name: 'Title', type: 'title' });
          for (const col of dbInfo.columns) {
            if (col.id !== 'title' && !excludedTypes.includes(col.type)) {
              fields.push({ id: col.id, name: col.name, type: col.type });
            }
          }
        }
      } else {
        const databases = this.store.getBlocksByFlavour('affine:database');
        const targetDb = databases.find(
          (db: { id: string }) => db.id === relation.targetDatabaseId
        );

        if (targetDb) {
          const model = (targetDb as { model?: unknown }).model ?? targetDb;
          const {
            createDatabaseBlockDataSource,
          } = require('@blocksuite/affine/blocks/database');
          const dataSource = createDatabaseBlockDataSource(model);

          fields.push({ id: 'title', name: 'Title', type: 'title' });

          const properties = dataSource.getBoundedPropertyIdsWindow()
            .propertyIds as string[];
          for (const propId of properties) {
            if (propId !== 'title') {
              const propType = dataSource.propertyTypeGet(propId);
              // Skip relation and rollup types
              if (excludedTypes.includes(propType)) continue;
              fields.push({
                id: propId,
                name: dataSource.propertyNameGet(propId) || 'Unnamed',
                type: propType || 'unknown',
              });
            }
          }
        }
      }
    } catch (e) {
      console.error('[RollupConfig] Error loading target fields:', e);
    }

    this._targetFields = fields;
  }

  /**
   * Handle relation selection
   */
  private _handleRelationChange(value: string) {
    this._selectedRelationId = value;
    this._selectedTargetFieldId = '';
    this._loadTargetFields().catch(console.error);
  }

  /**
   * Handle target field selection
   */
  private _handleTargetFieldChange(value: string) {
    this._selectedTargetFieldId = value;
  }

  /**
   * Handle aggregation selection
   */
  private _handleAggregationChange(value: string) {
    this._selectedAggregation = value as RollupAggregation;
  }

  /**
   * Handle save
   */
  private _handleSave() {
    if (!this._selectedRelationId || !this._selectedTargetFieldId) return;

    const updateData = {
      sourceRelationFieldId: this._selectedRelationId,
      targetFieldId: this._selectedTargetFieldId,
      aggregation: this._selectedAggregation,
    };

    this.onUpdate(updateData);
    this.onClose?.();
  }

  /**
   * Convert relation columns to dropdown options
   */
  private get relationOptions(): DropdownOption[] {
    return this._relationColumns.map(rel => ({
      id: rel.id,
      label: rel.name,
    }));
  }

  /**
   * Convert target fields to dropdown options
   */
  private get targetFieldOptions(): DropdownOption[] {
    return this._targetFields.map(field => ({
      id: field.id,
      label: `${field.name} (${field.type})`,
    }));
  }

  /**
   * Get aggregation options filtered by target field type
   */
  private get aggregationOptions(): DropdownOption[] {
    const targetField = this._targetFields.find(
      f => f.id === this._selectedTargetFieldId
    );
    const targetType = targetField?.type || 'unknown';

    const typeMapping: Record<string, 'number' | 'string' | 'date' | 'all'> = {
      number: 'number',
      text: 'string',
      title: 'string',
      'rich-text': 'string',
      date: 'date',
      checkbox: 'all',
      select: 'all',
      'multi-select': 'all',
      relation: 'all',
      rollup: 'all',
    };

    const applicableType = typeMapping[targetType] || 'all';

    return (
      Object.entries(AGGREGATION_META) as Array<
        [
          RollupAggregation,
          { label: string; icon: string; applicableTo: string[] },
        ]
      >
    )
      .filter(
        ([_, meta]) =>
          meta.applicableTo.includes('all') ||
          meta.applicableTo.includes(applicableType)
      )
      .map(([value, meta]) => ({
        id: value,
        label: meta.label,
        icon: meta.icon,
      }));
  }

  override render() {
    const isValid = this._selectedRelationId && this._selectedTargetFieldId;
    const stopProp = (e: Event) => e.stopPropagation();

    if (this._relationColumns.length === 0) {
      return html`
        <div class="rollup-config" @click=${stopProp} @mousedown=${stopProp}>
          <div class="rollup-config-header">
            ${RollupIcon}
            <span>Configure Rollup</span>
          </div>
          <div class="rollup-config-empty">
            No relation columns found.<br />
            Create a relation column first to use rollups.
          </div>
        </div>
      `;
    }

    return html`
      <div class="rollup-config" @click=${stopProp} @mousedown=${stopProp}>
        <div class="rollup-config-header">
          ${RollupIcon}
          <span>Configure Rollup</span>
        </div>

        <!-- Source Relation -->
        <div class="rollup-config-section">
          <label class="rollup-config-label">Source Relation</label>
          <config-dropdown
            .options=${this.relationOptions}
            .value=${this._selectedRelationId}
            .placeholder=${'Select a relation...'}
            .onChange=${(value: string) => this._handleRelationChange(value)}
          ></config-dropdown>
          <div class="rollup-config-hint">
            Choose which relation column to use for the rollup
          </div>
        </div>

        <!-- Target Field -->
        <div class="rollup-config-section">
          <label class="rollup-config-label">Target Field</label>
          <config-dropdown
            .options=${this.targetFieldOptions}
            .value=${this._selectedTargetFieldId}
            .placeholder=${'Select a field...'}
            .disabled=${!this._selectedRelationId ||
            this._targetFields.length === 0}
            .onChange=${(value: string) => this._handleTargetFieldChange(value)}
          ></config-dropdown>
          <div class="rollup-config-hint">
            Choose which field from the related table to show
          </div>
        </div>

        <div class="rollup-config-divider"></div>

        <!-- Aggregation -->
        <div class="rollup-config-section">
          <label class="rollup-config-label">Aggregation</label>
          <config-dropdown
            .options=${this.aggregationOptions}
            .value=${this._selectedAggregation}
            .placeholder=${'Select aggregation...'}
            .disabled=${!this._selectedTargetFieldId}
            .onChange=${(value: string) => this._handleAggregationChange(value)}
          ></config-dropdown>
          <div class="rollup-config-hint">
            How to combine values when multiple items are linked
          </div>
        </div>

        <!-- Save Button -->
        <button
          class="rollup-config-save"
          ?disabled=${!isValid}
          @click=${() => this._handleSave()}
        >
          Save Configuration
        </button>
      </div>
    `;
  }
}
